import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient, AdminUserDetailOut } from '../../services/apiClient';
import ConfirmAction from './ConfirmAction';
import { fmtDate } from './format';

interface Props {
  userId: string;
  onClose: () => void;
  onChanged: () => void;
}

/** Which destructive action is currently armed, keyed by order/subscription id. */
type Armed = { action: 'revoke' | 'endComp'; id: string } | null;

const AdminUserDrawer: React.FC<Props> = ({ userId, onClose, onChanged }) => {
  const api = useApiClient();
  const [detail, setDetail] = useState<AdminUserDetailOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState<Armed>(null);
  const [compUntil, setCompUntil] = useState(() => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10));
  const drawerRef = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await api.getAdminUserDetail(userId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      // Keep Tab inside the drawer. Without this, tabbing past the last button
      // lands on the table behind the backdrop — focus goes somewhere the user
      // cannot see and cannot click.
      const root = drawerRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Move focus into the drawer on open and hand it back on close, so a
  // keyboard user isn't dropped at the top of the document afterwards.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    drawerRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  // Every action refetches rather than patching local state. At this scale the
  // extra request is free and it removes a whole class of stale-UI bugs.
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setArmed(null);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const isArmed = (action: 'revoke' | 'endComp', id: string) =>
    armed?.action === action && armed?.id === id;

  return (
    <>
      {/* Decorative: Escape and the close button are the keyboard paths out. */}
      <div className="admin-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside
        ref={drawerRef}
        className="admin-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={detail ? `User detail for ${detail.email}` : 'User detail'}
        tabIndex={-1}
      >
        <button
          type="button"
          className="admin-drawer-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {loading && <p className="admin-muted">Loading…</p>}
        {error && <p className="admin-error">{error}</p>}

        {detail && (
          <>
            <h2 className="admin-drawer-title">{detail.name || '—'}</h2>
            <p className="admin-drawer-email">{detail.email}</p>

            <dl className="admin-facts">
              <dt>Sign-in</dt>
              <dd>{detail.providers.length ? detail.providers.join(', ') : 'password'}</dd>
              <dt>Verified</dt>
              <dd>{detail.email_verified ? 'yes' : 'no'}</dd>
              <dt>User id</dt>
              <dd className="admin-mono">{detail.id}</dd>
              <dt>Stripe</dt>
              <dd className="admin-mono">{detail.stripe_customer_id || '—'}</dd>
              <dt>Access</dt>
              <dd>{detail.access ?? 'none'}</dd>
              <dt>Onboarding</dt>
              <dd>{detail.onboarding_status.replace(/_/g, ' ')}</dd>
              <dt>Joined</dt>
              <dd>{fmtDate(detail.created_at)}</dd>
            </dl>

            <h3 className="admin-drawer-subtitle">Subscriptions ({detail.subscriptions.length})</h3>
            {detail.subscriptions.length === 0 && <p className="admin-muted">None.</p>}
            <ul className="admin-orders">
              {detail.subscriptions.map((s) => (
                <li key={s.id} className="admin-order">
                  <div className="admin-order-head">
                    <span>{s.source === 'admin_comp' ? 'comp' : s.plan}</span>
                    <span className="admin-order-status">{s.status}</span>
                    <span>{s.cancel_at_period_end ? 'ends' : 'until'} {fmtDate(s.current_period_end)}</span>
                  </div>
                  {s.stripe_subscription_id && (
                    <div className="admin-order-meta admin-mono">{s.stripe_subscription_id}</div>
                  )}
                  {s.source === 'admin_comp' && s.status === 'active' && (
                    <ConfirmAction
                      label="End comp"
                      confirmLabel="Really end? Yes"
                      armed={isArmed('endComp', s.id)}
                      disabled={busy}
                      onArm={() => setArmed({ action: 'endComp', id: s.id })}
                      onCancel={() => setArmed(null)}
                      onConfirm={() => run(() => api.endComp(s.id))}
                    />
                  )}
                </li>
              ))}
            </ul>

            <h3 className="admin-drawer-subtitle">Legacy credits & reports ({detail.orders.length})</h3>
            {detail.orders.length === 0 && <p className="admin-muted">No orders yet.</p>}

            <ul className="admin-orders">
              {detail.orders.map((o) => (
                <li key={o.id} className="admin-order">
                  <div className="admin-order-head">
                    <span>{fmtDate(o.created_at)}</span>
                    <span className="admin-order-status">{o.status}</span>
                    {o.source === 'admin_grant' && (
                      <span className="admin-order-granted">granted</span>
                    )}
                    {o.fit_score != null && <span>fit {o.fit_score}</span>}
                  </div>
                  <div className="admin-order-meta admin-mono">
                    {o.stripe_session_id || 'no stripe session'}
                  </div>

                  {o.status === 'purchased' && (
                    <ConfirmAction
                      label="Revoke"
                      confirmLabel="Really revoke? Yes"
                      armed={isArmed('revoke', o.id)}
                      disabled={busy}
                      onArm={() => setArmed({ action: 'revoke', id: o.id })}
                      onCancel={() => setArmed(null)}
                      onConfirm={() => run(() => api.revokeOrder(o.id))}
                    />
                  )}
                </li>
              ))}
            </ul>

            <label className="admin-comp-label" htmlFor="comp-until">Comp access until</label>
            <input
              id="comp-until"
              type="date"
              className="admin-search"
              value={compUntil}
              onChange={(e) => setCompUntil(e.target.value)}
            />
            <button
              type="button"
              className="admin-action admin-action--primary"
              disabled={busy || !compUntil}
              onClick={() => run(() => api.compAccess(detail.id, compUntil))}
            >
              Comp access
            </button>

            {/*
              The drawer scrolls and this button sits near the bottom, so the
              top-of-drawer error above can be scrolled out of view when a
              write fails. Render the same `error` again right next to the
              button that triggers it, so a failed grant is always visible
              wherever the admin's focus already is.
            */}
            {error && <p className="admin-error">{error}</p>}
            <button
              type="button"
              className="admin-action"
              disabled={busy}
              onClick={() => run(() => api.grantCredit(detail.id))}
            >
              Grant free credit
            </button>
          </>
        )}
      </aside>
    </>
  );
};

export default AdminUserDrawer;
